/* eslint-disable react/display-name */
import React, { useContext } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { GridInitialState, UnitsContext } from '@innovyze/stylovyze';
import { RangeCell } from './RangeCell';
import { SkeletonCell } from './SkeletonCell';
import { useGlobalization } from '@Translations/useGlobalization';
import { selectIsCountLoading } from '@Selectors/Risk.selectors';
import { MEASUREMENT_SYSTEM } from '@Utils/units/unitsMappingValues';

export const getWidth = (
	field: string,
	dataGridState?: GridInitialState,
): number | undefined => {
	// Get the width of the column from the saved state if available
	if (
		dataGridState &&
		dataGridState.columns?.dimensions &&
		dataGridState.columns.dimensions[field]
	) {
		return dataGridState.columns.dimensions[field].width;
	} else return undefined;
};

export const getFlex = (
	field: string,
	flex: number,
	dataGridState?: GridInitialState,
): number | undefined => {
	// If there is a saved width then return undefined
	// We dont want to specify both flex and width since flex will override the width
	if (
		dataGridState &&
		dataGridState.columns?.dimensions &&
		dataGridState.columns.dimensions[field] &&
		dataGridState.columns.dimensions[field].width
	) {
		return undefined;
	} else return flex;
};

export const tableColumns = (
	onRangeChange: (index: number, value: number, valueUnit?: number) => void,
	countKey: string,
	unit?: string,
	currency?: string,
	precision?: number,
) => {
	const { t } = useGlobalization();
	const { system } = useContext(UnitsContext);
	const lengthUnit =
		system === MEASUREMENT_SYSTEM.METRIC ? t('Kilometers') : t('Miles');

	const isRiskCountLoading = selectIsCountLoading(countKey);

	const header = new Array<GridColDef>();

	if (t) {
		header.push(
			{
				field: 'value',
				headerName: t('Ranges'),
				sortable: false,
				hideable: false,
				align: 'left',
				flex: 1.5,
				minWidth: 200,
				renderCell: params => {
					return (
						<RangeCell
							id={params.id}
							value={params.value}
							onRangeChange={onRangeChange}
							disabled={isRiskCountLoading}
							unit={unit}
							currency={currency}
							precision={precision}
						/>
					);
				},
			},
			{
				field: 'count',
				headerName: t('Asset Count'),
				sortable: false,
				hideable: false,
				align: 'left',
				headerAlign: 'left',
				type: 'number',
				flex: 1,
				renderCell: params => {
					return (
						<SkeletonCell
							id={params.id}
							countKey={countKey}
							value={params.value}
							percent={false}
						/>
					);
				},
			},
			{
				field: 'countPercent',
				headerName: t('Asset Count (%)'),
				sortable: false,
				hideable: false,
				align: 'left',
				headerAlign: 'left',
				flex: 1,
				renderCell: params => {
					return (
						<SkeletonCell
							id={params.id}
							countKey={countKey}
							value={params.value}
							percent={false}
						/>
					);
				},
			},
			{
				field: 'length',
				headerName: t('Length ({{unit}})', { unit: lengthUnit }),
				sortable: false,
				hideable: false,
				align: 'left',
				headerAlign: 'left',
				type: 'number',
				flex: 1,
				renderCell: params => {
					return (
						<SkeletonCell
							id={params.id}
							countKey={countKey}
							value={params.value}
							percent={false}
						/>
					);
				},
			},
			{
				field: 'lengthPercent',
				headerName: t('Length (%)'),
				sortable: false,
				hideable: false,
				align: 'left',
				headerAlign: 'left',
				flex: 1,
				renderCell: params => {
					return (
						<SkeletonCell
							id={params.id}
							countKey={countKey}
							value={params.value}
							percent={false}
						/>
					);
				},
			},
		);
	}

	return header;
};
